<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FormSelectGroup from "@/components/molecules/form/FormSelectGroup.vue";
import ExternalLink from "@/const/externalLink";
import {
  endOfMonth,
  formatFromUnixTimestamp,
  getYear,
  now,
  parseISO
} from "@/utils/date";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";

defineProps({
  isShownInputModal: {
    type: Boolean,
    required: true
  },
  isShownIncorrectModal: {
    type: Boolean,
    default: false
  }
});

const birthDateYear = ref<number>(1990);
const birthDateMonth = ref<number>(12);
const birthDateDay = ref<number | null>(31);
const vObserver = ref<CustomValidationObserver | null>(null);
const birthDateDayElement = ref<FormSelectGroup | null>(null);

const formValues = computed(() => ({
  birthDateYear: birthDateYear.value,
  birthDateMonth: birthDateMonth.value,
  birthDateDay: birthDateDay.value
}));
const birthDateYearOptions = computed(() => {
  const currentYear = getYear(now());
  const yearOptions = Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, text: `${year}年` };
  }).reverse();
  return yearOptions;
});
const birthDateMonthOptions = computed(() => {
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    return { value: month, text: `${month}月` };
  });
  return monthOptions;
});
const birthDateDayOptions = computed(() => {
  // 月末日を取得
  const lastDayOfMonth = Number(
    formatFromUnixTimestamp(
      endOfMonth(
        parseISO(
          `${birthDateYear.value}-${birthDateMonth.value
            .toString()
            .padStart(2, "0")}-01`
        )
      ),
      "d"
    )
  );
  const dayOptions = Array.from({ length: lastDayOfMonth }, (_, i) => {
    const day = i + 1;
    return { value: day, text: `${day}日` };
  });
  return dayOptions;
});
const birthDate = computed(() => {
  if (birthDateDay.value === null) {
    return null;
  }
  return `${birthDateYear.value}-${birthDateMonth.value
    .toString()
    .padStart(2, "0")}-${birthDateDay.value.toString().padStart(2, "0")}`;
});

watch(
  () => [birthDateYear.value, birthDateMonth.value],
  () => {
    if (!birthDateDay.value) return;
    // 選択した月に存在しない日付が入力されていた場合、未入力に戻す
    if (!birthDateDayOptions.value.some(o => o.value === birthDateDay.value)) {
      birthDateDay.value = null;
      // NOTE: 誕生日のバリデーションを走らせるために handleChange を呼ぶ
      birthDateDayElement.value!.validationState.handleChange(null, true);
    }
  }
);

const emits = defineEmits(["close", "ok"]);

function onSubmit() {
  emits("ok", birthDate.value);
}
</script>

<template>
  <CustomValidationObserver
    :initial-values="formValues"
    v-slot="{ handleSubmit }"
    ref="vObserver"
  >
    <BModal
      :visible="isShownInputModal"
      centered
      ok-only
      hide-header
      no-close-on-backdrop
      no-close-on-esc
      size="md"
      content-class="mx-4"
      footer-class="d-flex justify-content-center py-2"
    >
      <div class="mt-4 mx-2 mb-2 text-center">
        <p class="mb-3 font-weight-bold">
          本人確認のため、<br />生年月日を教えてください
        </p>
        <BRow class="mx-n2">
          <BCol cols="4" class="px-2">
            <ValidationProvider
              v-slot="validationState"
              name="誕生年"
              rules="required"
              vid="birthDateYear"
              v-model="birthDateYear"
            >
              <FormSelectGroup
                v-model="birthDateYear"
                :options="birthDateYearOptions"
                firstOption="年"
                class="mb-3 form-select"
                :validation-state="validationState"
                data-testid="birthDateYear"
              />
            </ValidationProvider>
          </BCol>
          <BCol cols="4" class="px-2">
            <ValidationProvider
              v-slot="validationState"
              name="誕生月"
              rules="required"
              vid="birthDateMonth"
              v-model="birthDateMonth"
            >
              <FormSelectGroup
                label=""
                v-model="birthDateMonth"
                :options="birthDateMonthOptions"
                firstOption="月"
                class="mb-3 form-select"
                :validation-state="validationState"
                data-testid="birthDateMonth"
              />
            </ValidationProvider>
          </BCol>
          <BCol cols="4" class="px-2">
            <ValidationProvider
              v-slot="validationState"
              name="誕生日"
              rules="required"
              vid="birthDateDay"
              v-model="birthDateDay"
            >
              <FormSelectGroup
                v-model="birthDateDay"
                :options="birthDateDayOptions"
                firstOption="日"
                class="mb-3 form-select"
                :validation-state="validationState"
                data-testid="birthDateDay"
                ref="birthDateDayElement"
              />
            </ValidationProvider>
          </BCol>
        </BRow>
      </div>
      <template #modal-footer>
        <AppButton class="px-5 py-2" @click="handleSubmit(onSubmit)">
          OK
        </AppButton>
      </template>
    </BModal>
  </CustomValidationObserver>
  <BModal
    :visible="isShownIncorrectModal"
    centered
    ok-only
    hide-header
    no-close-on-backdrop
    no-close-on-esc
    size="md"
    content-class="mx-2"
    footer-class="d-flex justify-content-center py-2"
  >
    <div class="mt-4 mx-2 mb-2 text-center">
      <p class="mb-3 font-weight-bold">
        生年月日が間違っています。<br />復帰を希望される場合は、<br />運営事務局へお問い合わせください。
      </p>
      <AppButtonOutline
        :href="ExternalLink.CONTACT"
        class="mt-1 mb-3"
        size="small"
        block
      >
        お問い合わせフォーム
      </AppButtonOutline>
    </div>
    <template #modal-footer>
      <AppButton class="px-5 py-2" @click="emits('close')">
        OK
      </AppButton>
    </template>
  </BModal>
</template>

<style lang="scss" scoped>
.form-select {
  :deep(select) {
    font-size: 12px;
  }
}

:deep(.invalid-feedback) {
  font-size: 12px;
}
</style>
